import { melodyGrids, melodyInstrumentHints, scaleNames, standardNoteNames } from '@tunasong/schemas'
import type { Melody, MelodyGrid, MelodyInstrumentHint, NoteName, ScaleType } from '@tunasong/schemas'
import { Button, FormControlLabel, Select, Switch, TextField } from '@tunasong/ui-lib'
import { useRef } from 'react'
import type { ChangeEvent, FC } from 'react'
import styles from './melody-roll-header.module.css'
import { bpmFromTaps } from './tap-tempo.js'

/**
 * The melody roll's header: tempo (typed or tapped), meter, grid, key, swing, instrument hint, and
 * the "quantized" A/B toggle.
 *
 * Every control writes a whole melody back through `onChange` - the header owns no melody state of
 * its own, so there is no second copy to fall out of step with the entity. The only state it does
 * own is the tap history, which is not part of the melody.
 *
 * Out-of-range tempi are dropped rather than clamped: a half-typed "9" on the way to "90" should
 * not save 20 bpm and then fight the user's next keystroke.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

export interface MelodyRollHeaderProps {
  melody: Melody
  onChange: (melody: Melody) => void
  quantized: boolean
  onQuantizedChange: (quantized: boolean) => void
  className?: string
}

const BEATS_PER_BAR = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

const GRID_LABEL: Record<MelodyGrid, string> = {
  '8': '1/8',
  '16': '1/16',
  '8t': '1/8 triplet',
  '16t': '1/16 triplet',
}

const HINT_LABEL: Record<MelodyInstrumentHint, string> = {
  voice: 'Voice',
  piano: 'Piano',
  guitar: 'Guitar',
}

/** A tap run is finished when the player stops; a fresh tap after this long starts a new count. */
const TAP_RESET_MS = 3000

export const MelodyRollHeader: FC<MelodyRollHeaderProps> = props => {
  const { melody, onChange, quantized, onQuantizedChange, className } = props
  const taps = useRef<number[]>([])

  const handleBpm = (event: ChangeEvent<HTMLInputElement>) => {
    const bpm = Number(event.target.value)
    if (!Number.isFinite(bpm) || bpm < 20 || bpm > 300) {
      return
    }
    onChange({ ...melody, tempo: { ...melody.tempo, bpm } })
  }

  const handleTap = () => {
    const now = performance.now()
    const previous = taps.current[taps.current.length - 1]
    taps.current = previous !== undefined && now - previous > TAP_RESET_MS ? [now] : [...taps.current, now]
    const bpm = bpmFromTaps(taps.current)
    if (bpm !== null) {
      onChange({ ...melody, tempo: { ...melody.tempo, bpm } })
    }
  }

  const handleMeterTop = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...melody,
      tempo: { ...melody.tempo, meter: { ...melody.tempo.meter, top: Number(event.target.value) } },
    })
  }

  const handleMeterBottom = (event: ChangeEvent<HTMLSelectElement>) => {
    const bottom = event.target.value === '8' ? 8 : 4
    onChange({ ...melody, tempo: { ...melody.tempo, meter: { ...melody.tempo.meter, bottom } } })
  }

  const handleGrid = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...melody, grid: event.target.value as MelodyGrid })
  }

  const handleKeyRoot = (event: ChangeEvent<HTMLSelectElement>) => {
    const root = event.target.value
    if (!root) {
      const { key: _key, ...withoutKey } = melody
      onChange(withoutKey)
      return
    }
    onChange({ ...melody, key: { root: root as NoteName, type: melody.key?.type ?? 'major' } })
  }

  const handleKeyType = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!melody.key) {
      return
    }
    onChange({ ...melody, key: { ...melody.key, type: event.target.value as ScaleType } })
  }

  const handleHint = (event: ChangeEvent<HTMLSelectElement>) => {
    const hint = event.target.value
    if (!hint) {
      const { instrumentHint: _hint, ...withoutHint } = melody
      onChange(withoutHint)
      return
    }
    onChange({ ...melody, instrumentHint: hint as MelodyInstrumentHint })
  }

  const handleSwing = (event: ChangeEvent<HTMLInputElement>) => {
    const swing = Number(event.target.value)
    if (!Number.isFinite(swing) || swing < 0 || swing > 1) {
      return
    }
    if (swing === 0) {
      const { feel: _feel, ...withoutFeel } = melody
      onChange(withoutFeel)
      return
    }
    onChange({ ...melody, feel: { swing } })
  }

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <div className={styles.tempo}>
        <TextField
          className={styles.bpm}
          label="Tempo (BPM)"
          type="number"
          inputProps={{ min: 20, max: 300 }}
          value={melody.tempo.bpm}
          onChange={handleBpm}
        />
        <Button onClick={handleTap} aria-label="Tap tempo">
          Tap
        </Button>
      </div>

      <Select className={styles.control} label="Beats per bar" value={melody.tempo.meter.top} onChange={handleMeterTop}>
        {BEATS_PER_BAR.map(top => (
          <option key={top} value={top}>
            {top}
          </option>
        ))}
      </Select>

      <Select
        className={styles.control}
        label="Beat unit"
        value={melody.tempo.meter.bottom}
        onChange={handleMeterBottom}
      >
        <option value={4}>4</option>
        <option value={8}>8</option>
      </Select>

      <Select className={styles.control} label="Grid" value={melody.grid} onChange={handleGrid}>
        {melodyGrids.map(grid => (
          <option key={grid} value={grid}>
            {GRID_LABEL[grid]}
          </option>
        ))}
      </Select>

      <Select className={styles.control} label="Key" value={melody.key?.root ?? ''} onChange={handleKeyRoot}>
        <option value="">None</option>
        {standardNoteNames.map(note => (
          <option key={note} value={note}>
            {note}
          </option>
        ))}
      </Select>

      <Select
        className={styles.wide}
        label="Scale"
        value={melody.key?.type ?? 'major'}
        onChange={handleKeyType}
        disabled={!melody.key}
      >
        {scaleNames.map(name => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>

      <TextField
        className={styles.control}
        label="Swing"
        type="number"
        inputProps={{ min: 0, max: 1, step: 0.1 }}
        value={melody.feel?.swing ?? 0}
        onChange={handleSwing}
      />

      <Select className={styles.control} label="Instrument" value={melody.instrumentHint ?? ''} onChange={handleHint}>
        <option value="">None</option>
        {melodyInstrumentHints.map(hint => (
          <option key={hint} value={hint}>
            {HINT_LABEL[hint]}
          </option>
        ))}
      </Select>

      {/*
        `FormControlLabel` nests its own <label> around the switch's, so the visible text does not
        reach the input as an accessible name. Set `aria-label` explicitly rather than relying on
        the wrapper.
      */}
      <FormControlLabel
        label="Quantized"
        control={<Switch aria-label="Quantized" checked={quantized} onCheckedChange={onQuantizedChange} />}
      />
    </div>
  )
}
